/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Cell, Grid, HFlow, Icon, InfoLabel, Tag, Text, Theme, Tooltip, useStyles, VFlow } from 'bold-ui'
import { blue, green, turquoise } from 'bold-ui/lib/styles/colors'
import { AccordionButton } from 'components/accordion/AccordionButton'
import { DateTime } from 'components/date'
import { AccordionField } from 'components/form/field/AccordionField'
import { OrigemAtendimento } from 'graphql/types.generated'
import { CSSProperties, Fragment } from 'react'
import { AccordionItem, AccordionItemPanel } from 'react-accessible-accordion'
import { covidImunosRecord } from 'view/lote-imunobiologico/insert/LoteImunologicoTooltipAddForm'

import { CondicoesFormModel, isBcg, origemDadosVacinacao } from './model'
import { SubModalVacinacaoOptions } from './VacinacaoModal'

// dates as strings just for now
export interface RegistroDoseProps {
  idUnicoRegistro: ID
  imunobiologicoId: string
  dataRegistro: string
  dataAplicacao: string
  dataValidadeLote: string
  estrategia: string
  nomeProfissional: string
  cbo: string
  dataAprazamento: string
  lote: string
  nomeFabricante: string
  viaDeAdministracao: string
  localDaAplicacao: string
  observacoes: string
  isGestante: boolean
  isPuerpera: boolean
  isViajante: boolean
  isComunicanteHanseniase: boolean
  grupoAtendimento?: string
  unidadeSaude: string
  origemDados: OrigemAtendimento
  isRegistroAnterior: boolean
  isAplicadoExterior: boolean
  openedAccordions?: (string | number)[]
  registradoAtendimento: boolean
  condicoesVacinacao: CondicoesFormModel
  handleOnDelete(idUnicoRegistro: ID, tipoModal: SubModalVacinacaoOptions): void
  handleOnEdit(idUnicoRegistro: ID, tipoModal: SubModalVacinacaoOptions): void
}

export const RegistroDose = (props: RegistroDoseProps) => {
  const {
    imunobiologicoId,
    dataRegistro,
    dataAplicacao,
    estrategia,
    nomeProfissional,
    cbo,
    lote,
    nomeFabricante,
    viaDeAdministracao,
    localDaAplicacao,
    observacoes,
    dataValidadeLote,
    isGestante,
    isPuerpera,
    isViajante,
    isComunicanteHanseniase,
    unidadeSaude,
    origemDados,
    grupoAtendimento,
    registradoAtendimento,
    idUnicoRegistro,
    handleOnDelete,
    handleOnEdit,
  } = props

  const { classes } = useStyles(createStyles)
  const { gestante, puerpera, viajante, comunicanteHanseniase } = props.condicoesVacinacao
  const hasCondicoesVacinacao =
    (registradoAtendimento && (gestante || puerpera || viajante || comunicanteHanseniase)) ||
    isGestante ||
    isPuerpera ||
    isViajante ||
    isComunicanteHanseniase

  return (
    <VFlow vSpacing={0}>
      <AccordionField name='Registro' allowZeroExpanded>
        <AccordionItem>
          <AccordionButton style={classes.accordionButton}>
            <HFlow
              alignItems='center'
              justifyContent='space-between'
              style={css`
                padding: 0.5rem;
                width: 35rem;
              `}
            >
              <HFlow>
                <Icon icon='angleDown' size={1.5} />
                <HFlow hSpacing={0.3} alignItems='center'>
                  <Icon color={green.c40} icon='needleFilled' size={1.1} />
                  <Text fontWeight='bold'>Dose aplicada em </Text>
                  <DateTime
                    value={dataAplicacao}
                    style={css`
                      font-weight: bold;
                    `}
                    format='DD/MM/YYYY'
                  />
                </HFlow>
              </HFlow>
              <HFlow hSpacing={0} alignItems='center'>
                <Tooltip text={origemDadosVacinacao[origemDados]?.hint}>
                  <Tag type='info'>{origemDadosVacinacao[origemDados]?.label}</Tag>
                </Tooltip>
                {registradoAtendimento && (
                  <VisualizacaoButtons
                    idUnicoRegistro={idUnicoRegistro}
                    tipoModal='APLICACAO'
                    handleOnDelete={handleOnDelete}
                    handleOnEdit={handleOnEdit}
                  />
                )}
              </HFlow>
            </HFlow>
          </AccordionButton>
          <AccordionItemPanel className={classes.panel}>
            <Grid
              gap={0}
              gapVertical={1}
              style={css`
                padding: 1rem;
                width: 36rem;
                word-wrap: break-word;
              `}
            >
              <Cell size={7}>
                <InfoLabel title='Estratégia'>{estrategia?.capitalize()}</InfoLabel>
              </Cell>
              {grupoAtendimento && (
                <Cell size={5}>
                  <InfoLabel title='Grupo de atendimento'> {grupoAtendimento.capitalize()} </InfoLabel>
                </Cell>
              )}
              <Cell size={7}>
                <InfoLabel title='Lote/Fabricante'>{`${lote?.capitalize()}/${nomeFabricante?.capitalize()}`}</InfoLabel>
              </Cell>
              <Cell size={5}>
                <InfoLabel title='Data de validade'>
                  <DateTime value={dataValidadeLote} format='DD/MM/YYYY' />
                </InfoLabel>
              </Cell>
              <Cell size={7}>
                <InfoLabel placeholder='-' title='Via de administração'>
                  {viaDeAdministracao?.capitalize()}
                </InfoLabel>
              </Cell>
              <Cell size={4}>
                <InfoLabel placeholder='-' title='Local da aplicação'>
                  {localDaAplicacao?.capitalize()}
                </InfoLabel>
              </Cell>
              <Cell size={12}>
                <InfoLabel title='Observações' placeholder='-'>
                  {observacoes}
                </InfoLabel>
              </Cell>
              {hasCondicoesVacinacao && (
                <Cell size={12}>
                  <InfoLabel title='Condições'>
                    <HFlow hSpacing={0.5}>
                      {((registradoAtendimento && puerpera) || isPuerpera) && (
                        <Tag style={classes.condicaoTag}>Puérpera</Tag>
                      )}
                      {((registradoAtendimento && gestante) || isGestante) && (
                        <Tag style={classes.condicaoTag}>Gestante</Tag>
                      )}
                      {((registradoAtendimento && viajante) || isViajante) && (
                        <Tag style={classes.condicaoTag}>Viajante</Tag>
                      )}
                      {((registradoAtendimento && comunicanteHanseniase) || isComunicanteHanseniase) &&
                        isBcg(imunobiologicoId) && <Tag style={classes.condicaoTag}> Comunicante de hanseníase </Tag>}
                    </HFlow>
                  </InfoLabel>
                </Cell>
              )}
              <Cell size={12}>
                <InfoLabel title='Aplicado por'>{`${nomeProfissional} (${cbo})`}</InfoLabel>
                {unidadeSaude}
              </Cell>
              <Cell>
                <Text fontWeight='bold'>Aplicação registrada em </Text>
                <DateTime
                  value={dataRegistro}
                  style={css`
                    font-weight: bold;
                  `}
                  format='DD/MM/YYYY [às] HH:mm'
                />
              </Cell>
            </Grid>
          </AccordionItemPanel>
        </AccordionItem>
      </AccordionField>
    </VFlow>
  )
}

export const AprazamentoDose = (props: RegistroDoseProps) => {
  const {
    dataRegistro,
    dataAprazamento,
    estrategia,
    nomeProfissional,
    cbo,
    unidadeSaude,
    origemDados,
    registradoAtendimento,
    idUnicoRegistro,
    handleOnDelete,
    handleOnEdit,
  } = props

  const { classes } = useStyles(createStyles)

  return (
    <VFlow vSpacing={0}>
      <AccordionField name='Registro' allowZeroExpanded>
        <AccordionItem>
          <AccordionButton style={classes.accordionButton}>
            <HFlow
              alignItems='center'
              justifyContent='space-between'
              style={css`
                padding: 0.5rem;
                width: 35rem;
              `}
            >
              <HFlow>
                <Icon icon='angleDown' size={1.5} />
                <HFlow hSpacing={0.3}>
                  <Icon color={blue.c40} icon='calendarFilled' size={1.1} />
                  <Text fontWeight='bold'>Dose aprazada para </Text>
                  <DateTime
                    value={dataAprazamento}
                    style={css`
                      font-weight: bold;
                    `}
                    format='DD/MM/YYYY'
                  />
                </HFlow>
              </HFlow>
              <HFlow hSpacing={0} alignItems='center'>
                <Tooltip text={origemDadosVacinacao[origemDados]?.hint}>
                  <Tag type='info'>{origemDadosVacinacao[origemDados]?.label}</Tag>
                </Tooltip>
                {registradoAtendimento && (
                  <VisualizacaoButtons
                    idUnicoRegistro={idUnicoRegistro}
                    tipoModal='APRAZAMENTO'
                    handleOnDelete={handleOnDelete}
                    handleOnEdit={handleOnEdit}
                  />
                )}
              </HFlow>
            </HFlow>
          </AccordionButton>
          <AccordionItemPanel className={classes.panel}>
            <Grid
              gap={0}
              gapVertical={1}
              style={css`
                padding: 1rem;
                width: 36rem;
              `}
            >
              <Cell size={12}>
                <InfoLabel title='Estratégia'>{estrategia?.capitalize()}</InfoLabel>
              </Cell>
              <Cell>
                <InfoLabel title='Aprazado por'>{`${nomeProfissional} (${cbo})`}</InfoLabel>
                {unidadeSaude}
              </Cell>
              <Cell size={12}>
                <Text fontWeight='bold'>Aprazamento registrado em </Text>
                <DateTime
                  value={dataRegistro}
                  style={css`
                    font-weight: bold;
                  `}
                  format='DD/MM/YYYY [às] HH:mm'
                />
              </Cell>
            </Grid>
          </AccordionItemPanel>
        </AccordionItem>
      </AccordionField>
    </VFlow>
  )
}

export const RegistroAnteriorDose = (props: RegistroDoseProps) => {
  const {
    dataRegistro,
    dataAplicacao,
    nomeProfissional,
    cbo,
    lote,
    nomeFabricante,
    observacoes,
    unidadeSaude,
    origemDados,
    registradoAtendimento,
    idUnicoRegistro,
    estrategia,
    isAplicadoExterior,
    imunobiologicoId,
    handleOnDelete,
    handleOnEdit,
  } = props

  const { classes } = useStyles(createStyles)
  const isCovid = covidImunosRecord[imunobiologicoId]

  return (
    <VFlow vSpacing={0}>
      <AccordionField name='Registro' allowZeroExpanded>
        <AccordionItem>
          <AccordionButton style={classes.accordionButton}>
            <HFlow
              alignItems='center'
              justifyContent='space-between'
              style={css`
                padding: 0.5rem;
                width: 35rem;
              `}
            >
              <HFlow>
                <Icon icon='angleDown' size={1.5} />
                <HFlow hSpacing={0.3} alignItems='center'>
                  <Icon color={green.c40} icon='needleFilled' size={1.1} />
                  <Text fontWeight='bold'>Dose aplicada em </Text>
                  <DateTime
                    value={dataAplicacao}
                    style={css`
                      font-weight: bold;
                    `}
                    format='DD/MM/YYYY'
                  />
                </HFlow>
              </HFlow>
              <HFlow hSpacing={0} alignItems='center'>
                <Tooltip text={origemDadosVacinacao[origemDados]?.hint}>
                  <Tag type='info'>{origemDadosVacinacao[origemDados]?.label}</Tag>
                </Tooltip>
                {registradoAtendimento && (
                  <VisualizacaoButtons
                    idUnicoRegistro={idUnicoRegistro}
                    tipoModal='ANTERIOR'
                    handleOnDelete={handleOnDelete}
                    handleOnEdit={handleOnEdit}
                  />
                )}
              </HFlow>
            </HFlow>
          </AccordionButton>
          <AccordionItemPanel className={classes.panel}>
            <Grid
              gap={0}
              gapVertical={1}
              style={css`
                padding: 1rem;
                width: 36rem;
                word-wrap: break-word;
              `}
            >
              {isCovid && (
                <Cell size={5}>
                  <InfoLabel title='Estratégia' placeholder='-'>
                    {estrategia?.capitalize()}
                  </InfoLabel>
                </Cell>
              )}
              <Cell size={7}>
                <InfoLabel title='Lote/Fabricante'>
                  {`${lote?.capitalize() || ' - '}/${nomeFabricante?.capitalize() || ' - '}`}
                </InfoLabel>
              </Cell>
              {isCovid && (
                <Cell size={12}>
                  <InfoLabel title='Aplicado no exterior' placeholder='-'>
                    {isAplicadoExterior ? 'Sim' : 'Não'}
                  </InfoLabel>
                </Cell>
              )}
              <Cell size={12}>
                <InfoLabel title='Observações' placeholder='-'>
                  {observacoes}
                </InfoLabel>
              </Cell>
              <Cell size={12}>
                <InfoLabel title='Registrado por'>{`${nomeProfissional} (${cbo})`}</InfoLabel>
                {unidadeSaude}
              </Cell>
              <Cell size={9}>
                <Text component='b'>
                  Aplicação registrada em
                  <DateTime
                    value={dataRegistro}
                    style={css`
                      font-weight: bold;
                    `}
                    format=' DD/MM/YYYY [às] HH:mm'
                  />
                </Text>
              </Cell>
              <Cell size={3}>
                <Tag type='normal' icon='undo'>
                  Registro anterior
                </Tag>
              </Cell>
            </Grid>
          </AccordionItemPanel>
        </AccordionItem>
      </AccordionField>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  panel: {
    margin: '0 0.3rem',
    borderWidth: '0 0.063rem 0.063rem',
    borderStyle: 'solid',
    borderColor: theme.pallete.gray.c60,
    position: 'relative',
    top: '-0.6rem',
  } as CSSProperties,
  accordionButton: {
    display: 'flex',
    padding: '0.5rem',
    border: `0.063rem solid ${theme.pallete.gray.c60}`,
    borderBlockEnd: `0.063rem solid ${theme.pallete.gray.c80}`,
  } as CSSProperties,
  condicaoTag: {
    backgroundColor: turquoise.c40,
    color: turquoise.c100,
  } as CSSProperties,
})

interface VisualizacaoButtonsProps {
  idUnicoRegistro: ID
  tipoModal: SubModalVacinacaoOptions
  handleOnDelete(idUnicoRegistro: ID, tipoModal: SubModalVacinacaoOptions): void
  handleOnEdit(idUnicoRegistro: ID, tipoModal: SubModalVacinacaoOptions): void
}

const VisualizacaoButtons = (props: VisualizacaoButtonsProps) => {
  const { idUnicoRegistro, tipoModal, handleOnDelete, handleOnEdit } = props

  return (
    <Fragment>
      <Tooltip text='Editar'>
        <Button
          kind='normal'
          onClick={(e) => {
            e.stopPropagation()
            handleOnEdit(idUnicoRegistro, tipoModal)
          }}
          size='small'
          skin='ghost'
        >
          <Icon icon='penOutline' />
        </Button>
      </Tooltip>
      <Tooltip text='Excluir'>
        <Button
          kind='danger'
          onClick={(e) => {
            e.stopPropagation()
            handleOnDelete(idUnicoRegistro, tipoModal)
          }}
          size='small'
          skin='ghost'
        >
          <Icon icon='trashOutline' />
        </Button>
      </Tooltip>
    </Fragment>
  )
}
